// ===========================================
// The Meet & Greet waitlist clock (#374 release B, Phase 5)
// ===========================================
// WaitlistClockInfo: "Waitlisted dd/mm/yyyy · review dd/mm/yyyy" and a chip once the review is
// near or due. The SERVER decides the state (`waitlistClock.state`); the browser only words it.
// WaitlistReviewActions: the OWNER's two actions on a waitlisted booking, Extend review and
// Confirm waitlist lapsed. Failures show inline and switch on `details.code`, never on the text.

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import type { MeetAndGreet, WaitlistClock, WaitlistConfirmResult } from '../../api/meetAndGreet.api';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { useConfirmWaitlistLapsed, useExtendWaitlistReview } from '../../hooks/useWaitlistReview';
import { brandColors, statusColors } from '../../styles/brandColors';
import { apiErrorCode, apiErrorMessage } from '../../utils/apiError';
import { formatInSchoolTz, schoolTodayUtcMidnight } from '../../utils/dateUtils';
import { enquiryOutcomeText } from '../admin/families/ForfeitRegistrationFeeDialog';

const DMY: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

/** A school-zone day of an instant as `YYYY-MM-DD`. */
function schoolDayKey(instant: string, timeZone: string): string {
  return formatInSchoolTz(instant, timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' }, 'en-CA');
}

const pad = (n: number) => String(n).padStart(2, '0');
const keyOf = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

/** `YYYY-MM-DD` plus one calendar month (31 Jan -> 28/29 Feb, never rolling into March). */
export function addOneMonthKey(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const target = new Date(Date.UTC(y, m, 1)); // first of next month
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return keyOf(target);
}

/** The school's today as `YYYY-MM-DD`. */
function schoolTodayKey(timeZone: string): string {
  return keyOf(schoolTodayUtcMidnight(timeZone));
}

/** The school's tomorrow as `YYYY-MM-DD`. */
function schoolTomorrowKey(timeZone: string): string {
  const t = schoolTodayUtcMidnight(timeZone);
  return keyOf(new Date(t.getTime() + 24 * 60 * 60 * 1000));
}

export function waitlistErrorText(error: unknown): string {
  switch (apiErrorCode(error)) {
    case 'MNG_NOT_WAITLISTED':
      return 'This booking is no longer on the waitlist. Refresh the page to see where it is now.';
    case 'WAITLIST_REVIEW_DAY_PAST':
      return 'The review day must be after today.';
    case 'WAITLIST_REVIEW_DAY_INVALID':
      return 'That is not a real day. Choose another.';
    case 'WAITLIST_REVIEW_DAY_TOO_FAR':
      return 'Choose a review day within 24 months.';
    default:
      return apiErrorMessage(error, 'Could not save');
  }
}

/** What the snackbar says after Confirm, from the response. */
export function waitlistLapsedText(result: WaitlistConfirmResult): string {
  const parts = ['Waitlist lapsed: the booking is rejected.'];
  if (result.forfeitedFeeId) parts.push('The school keeps the registration fee.');
  const about = enquiryOutcomeText(result.enquiry);
  if (about) parts.push(about);
  parts.push('The family was not emailed.');
  return parts.join(' ');
}

// -------------------------------------------
// Clock text + chip
// -------------------------------------------

export function WaitlistClockInfo({ clock }: { clock: WaitlistClock | null | undefined }) {
  const tz = useSchoolTimezone();
  if (!clock) return null;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" data-testid="waitlist-clock-text">
        Waitlisted {clock.estimated ? 'on or after ' : ''}{formatInSchoolTz(clock.waitlistedAt, tz, DMY)} · review {formatInSchoolTz(clock.reviewAt, tz, DMY)}
      </Typography>
      {clock.state === 'REVIEW_SOON' && (
        <Chip
          label="Review soon"
          size="small"
          sx={{ bgcolor: statusColors.infoBg, color: brandColors.textPrimary, fontWeight: 600 }}
        />
      )}
      {clock.state === 'REVIEW_DUE' && (
        <Chip
          label="Review due"
          size="small"
          sx={{ bgcolor: statusColors.warningBg, color: brandColors.textPrimary, fontWeight: 600 }}
        />
      )}
    </Box>
  );
}

// -------------------------------------------
// Owner actions
// -------------------------------------------

interface DialogProps {
  open: boolean;
  onClose: () => void;
  booking: MeetAndGreet;
  /** Called after a successful save (the page closes its stale detail dialog). */
  onDone?: () => void;
}

export function ExtendWaitlistDialog({ open, onClose, booking, onDone }: DialogProps) {
  const tz = useSchoolTimezone();
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useExtendWaitlistReview();
  const [reviewOn, setReviewOn] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const min = schoolTomorrowKey(tz);
  const currentReviewAt = booking.waitlistClock?.reviewAt;

  useEffect(() => {
    if (!open) return;
    // A month after the review, or after today when the review is long past (/qa P5 L2): never before tomorrow.
    const fromReview = currentReviewAt ? addOneMonthKey(schoolDayKey(currentReviewAt, tz)) : min;
    const fromToday = addOneMonthKey(schoolTodayKey(tz));
    setReviewOn(fromReview > fromToday ? fromReview : fromToday);
    setNote('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking.id, currentReviewAt]);

  const valid = /^\d{4}-\d{2}-\d{2}$/.test(reviewOn) && reviewOn >= min;

  const handleClose = () => {
    if (!mutation.isPending) onClose();
  };
  const handleSubmit = async () => {
    if (!valid) return;
    setError(null);
    try {
      await mutation.mutateAsync({ id: booking.id, reviewOn, note: note.trim() || undefined });
      const [y, m, d] = reviewOn.split('-');
      enqueueSnackbar(`Review moved to ${d}/${m}/${y}.`, { variant: 'success' });
      onClose();
      onDone?.();
    } catch (e) {
      setError(waitlistErrorText(e));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Extend review</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {booking.studentFirstName} {booking.studentLastName} stays on the waitlist. Choose the new day to review
            the booking.
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Review on"
            value={reviewOn}
            onChange={(e) => setReviewOn(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min }}
            disabled={mutation.isPending}
          />
          <TextField
            fullWidth
            size="small"
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            rows={2}
            disabled={mutation.isPending}
            inputProps={{ maxLength: 500 }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending} sx={{ minHeight: { xs: 44 } }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!valid || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ minHeight: { xs: 44 } }}
        >
          {mutation.isPending ? 'Saving...' : 'Extend review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ConfirmWaitlistLapsedDialog({ open, onClose, booking, onDone }: DialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useConfirmWaitlistLapsed();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open, booking.id]);

  const handleClose = () => {
    if (!mutation.isPending) onClose();
  };
  const handleSubmit = async () => {
    setError(null);
    try {
      const result = await mutation.mutateAsync({ id: booking.id });
      enqueueSnackbar(waitlistLapsedText(result), { variant: 'success' });
      onClose();
      onDone?.();
    } catch (e) {
      setError(waitlistErrorText(e));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm waitlist lapsed</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2">
            This ends {booking.studentFirstName} {booking.studentLastName}'s time on the waitlist:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            <li>The booking is rejected.</li>
            <li>The enquiry closes as Waitlist lapsed, if this was its last child.</li>
            <li>A held registration fee is kept by the school.</li>
            <li>The family is not emailed.</li>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending} sx={{ minHeight: { xs: 44 } }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ minHeight: { xs: 44 } }}
        >
          {mutation.isPending ? 'Saving...' : 'Confirm waitlist lapsed'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Owner only (the caller gates on `isOwner`): the two buttons and their dialogs. */
export function WaitlistReviewActions({ booking, onDone }: { booking: MeetAndGreet; onDone?: () => void }) {
  const [extendOpen, setExtendOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (booking.status !== 'WAITLISTED') return null;
  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" size="small" onClick={() => setExtendOpen(true)} sx={{ minHeight: { xs: 44 } }}>
          Extend review
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          onClick={() => setConfirmOpen(true)}
          sx={{ minHeight: { xs: 44 } }}
        >
          Confirm waitlist lapsed
        </Button>
      </Box>
      <ExtendWaitlistDialog open={extendOpen} onClose={() => setExtendOpen(false)} booking={booking} onDone={onDone} />
      <ConfirmWaitlistLapsedDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} booking={booking} onDone={onDone} />
    </>
  );
}
